import {
  adminSealVars,
  defaultSealVars,
  deleteSeals,
  ecopetrolSealVars,
  kocSealVars,
  restoreSeals
} from "@/models/equipment/seal-thrust-chambers/predicates";
import type { Seals } from "gql/api";
import {
  useDeleteSealsMutation,
  useInsertSealMutation,
  useRestoreSealsMutation,
  useSealThrustChambersQuery,
  useUpdateSealByPkMutation
} from "gql/api";
import { v4 } from "uuid";
import { computed } from "vue";
import { useEquipment } from "@/composables/equipment/baseEquipment";
import type { UseEquipment } from "@/composables/equipment/equipmentTypes";
import { sealTableColumns } from "./thrustTableColumns";
import useUserInfo from "@/composables/auth/useUserInfo";

export function useThrustChambersSeals(isAdmin: boolean): UseEquipment<Seals> {
  const baseEquipment = useEquipment<Seals>(isAdmin);
  const title = "Seal Thrust Chambers";
  const user = useUserInfo();

  const getVariables = () => {
    if (user.is_koc_customer) return kocSealVars;
    if (user.is_ecopetrol_customer) return ecopetrolSealVars;
    if (user.is_admin) return adminSealVars;
    return defaultSealVars;
  };

  const {
    data,
    executeQuery: refetch,
    stale: isStale
  } = useSealThrustChambersQuery({
    variables: getVariables()
  });

  const allRows = computed(() => data.value?.seals as Seals[] | undefined);

  const hashOrder: (keyof Seals)[] = [
    "manufacturer",
    "part_number",
    "configuration",
    "housing_material",
    "bearing_type",
    "description"
  ];

  const { defineVisibleColumns, defineFilterData } = baseEquipment;

  const { defineUpsert, defineRestore, defineDelete } =
    baseEquipment.defineMutationHandlers(refetch, hashOrder);

  const { executeMutation: executeInsertSeal } = useInsertSealMutation();
  const { executeMutation: executeUpdateSeal } = useUpdateSealByPkMutation();
  const { executeMutation: executeDeleteSeal } = useDeleteSealsMutation();
  const { executeMutation: executeRestoreSeal } = useRestoreSealsMutation();

  const insertSeal = (
    seal: Omit<Seals, "housings" | "sizing_import" | "user">
  ) => executeInsertSeal({ object: seal });

  const updateSeal = (id: string, seal: Seals) =>
    executeUpdateSeal({ id, object: seal });

  const deleteSeal = (ids: string[]) =>
    executeDeleteSeal({ where: deleteSeals(ids) });

  const restoreSeal = (ids: string[]) =>
    executeRestoreSeal({ where: restoreSeals(ids) });

  baseEquipment.defineEmptyRow(() => ({
    __typename: "seals",
    id: v4(),
    manufacturer: "",
    description: "",
    part_number: "",
    housing_material: "",
    bearing_type: "",
    configuration: "",
    outer_diameter_inches: null,
    neck_outer_diameter_inches: 1,
    thrust_collar_outer_diameter_inches: 1,
    thrust_collar_inner_diameter_inches: 1,
    length_inches: null,
    weight_pounds: null,
    max_temperature_fahrenheit: null,
    standard_shaft_limit_hp: null,
    high_strength_shaft_limit_hp: null,
    standard_bearing_rating_pounds: null,
    high_strength_bearing_rating_pounds: null,
    total_seal_volume_gallons: null,
    volume_for_bag_expansion_lower_tandem_gallons: null,
    volume_for_bag_expansion_upper_tandem_gallons: null,
    upper_chamber_pull_volume_gallons: null,
    bag_chambers: null,
    string_load_pounds: null,
    load_cb_pounds: null,
    load_c1_pounds: null,
    load_test_temperature_fahrenheit: null,
    bag_capacity_gallons: 0,
    created_at: "now()",
    updated_at: "now()",
    deleted_at: null,
    imported_sizing_id: null,
    hps_thrust_chamber: true
  }));

  return {
    searchPlaceholder: "Search Thrust Chambers...",
    searchToolTip: `Add quotes around search terms for a more exact result. Ex: "BPBSL"`,
    columns: defineVisibleColumns(sealTableColumns),
    handleUpsert: defineUpsert(insertSeal, updateSeal),
    handleDelete: defineDelete(deleteSeal),
    handleRestore: defineRestore(restoreSeal),
    totalCount: computed(() => data.value?.seals_aggregate.aggregate?.count),
    title,
    allRows,
    isAdmin,
    refetch,
    isStale,
    ...baseEquipment,
    ...defineFilterData({
      columns: sealTableColumns,
      globalSearchFields: ["configuration", "outer_diameter_inches"],
      allRows,
      title
    })
  };
}
